// AddIndicatorPicker kırılım kuralı (spec §6.3): kırılımlı gösterge (çocukları kırılım grubu olan
// metrik) seçilince grupları ve üyeleri otomatik işaretlenir — tek tek kaldırılabilir; üyesiz grup
// sete alınmaz; kısmi seçim grubu bu sette eksik bırakır ("kırılımın N üyesi sette yok").

import type { KpiMetric } from "@mahir/shared";

type Node = Pick<KpiMetric, "id" | "parentId" | "nodeKind">;

function childrenIndex<T extends Node>(metrics: readonly T[]): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const m of metrics) {
    if (!m.parentId) continue;
    const list = out.get(m.parentId) ?? [];
    list.push(m);
    out.set(m.parentId, list);
  }
  return out;
}

const isGroup = (m: Node | undefined): boolean => m?.nodeKind === "breakdown_group";

/** Seçim değişimi: toplam → grup + üyeler; grup → üyeler; üye seçilince grubu da. */
export function togglePickWithBreakdown<T extends Node>(
  metrics: readonly T[],
  picked: ReadonlySet<string>,
  id: string,
  checked: boolean,
  inSet: ReadonlySet<string>,
): Set<string> {
  const byId = new Map(metrics.map((m) => [m.id, m]));
  const kids = childrenIndex(metrics);
  const next = new Set(picked);
  const apply = (mid: string): void => {
    if (inSet.has(mid)) return;
    if (checked) next.add(mid);
    else next.delete(mid);
  };
  apply(id);
  const node = byId.get(id);
  const groups = isGroup(node) ? [node!] : (kids.get(id) ?? []).filter(isGroup);
  // toplam / grup → alt kırılım (toplam bırakılınca kırılımı da kalkar)
  for (const g of groups) {
    if (g.id !== id) apply(g.id);
    for (const mem of kids.get(g.id) ?? []) apply(mem.id);
  }
  // üye seçilince grubu (sette değilse) — üyesiz grup sete alınmaz kuralının tersi
  const parent = node?.parentId ? byId.get(node.parentId) : undefined;
  if (checked && isGroup(parent) && !inSet.has(parent!.id)) next.add(parent!.id);
  return next;
}

export interface BreakdownPickPlan {
  /** sete eklenecek seçimler (seçim sırası; üyesiz gruplar düşer) */
  ids: string[];
  /** sete girecek gruplardaki seçilmemiş ve sette olmayan üye sayısı */
  missingMembers: number;
  /** üyesi seçilmediği için atlanan grup sayısı */
  skippedGroups: number;
  /** seçimde en az bir kırılım grubu var (bilgi satırı) */
  hasBreakdown: boolean;
}

export function breakdownPickPlan<T extends Node>(
  metrics: readonly T[],
  picked: ReadonlySet<string>,
  inSet: ReadonlySet<string>,
): BreakdownPickPlan {
  const byId = new Map(metrics.map((m) => [m.id, m]));
  const kids = childrenIndex(metrics);
  const ids: string[] = [];
  let missingMembers = 0;
  let skippedGroups = 0;
  let hasBreakdown = false;
  const groupsInPlay = new Set<string>();
  for (const id of picked) {
    if (!isGroup(byId.get(id))) continue;
    const members = kids.get(id) ?? [];
    if (!members.some((mem) => picked.has(mem.id) || inSet.has(mem.id))) {
      skippedGroups += 1;
      continue;
    }
    groupsInPlay.add(id);
  }
  for (const id of picked) {
    const m = byId.get(id);
    if (isGroup(m) && !groupsInPlay.has(id)) continue;
    ids.push(id);
  }
  // sete girecek ya da zaten sette olan gruplarda eksik üye
  const touched = new Set(groupsInPlay);
  for (const id of picked) {
    const p = byId.get(id)?.parentId;
    if (p && isGroup(byId.get(p))) touched.add(p);
  }
  for (const g of touched) {
    hasBreakdown = true;
    for (const mem of kids.get(g) ?? [])
      if (!picked.has(mem.id) && !inSet.has(mem.id)) missingMembers += 1;
  }
  return { ids, missingMembers, skippedGroups, hasBreakdown };
}
